// ===============================================================
// ProBank — Cards Page (professional card centre)
//
// States:
//   1. No card               → flippable preview + benefits + Apply
//   2. Pending               → card + payment review timeline
//   3. Approved              → "Activate Your Card" → support
//   4. Activation requested  → awaiting support activation
//   5. Active / Frozen       → PIN required to view details,
//                              set PIN (first time) / change PIN
//   6. Rejected              → message + re-apply option
//
// The card PIN is the same 4-digit transaction PIN used for
// transfers (one PIN per user, like a real bank). Set / change /
// reset go through /api/pin — the backend hashes it with bcrypt.
// ===============================================================

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";
import VirtualCardDisplay from "../components/VirtualCardDisplay";
import BottomNav from "../components/BottomNav";

export default function CardsPage() {
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [hasPin, setHasPin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [previewFlipped, setPreviewFlipped] = useState(false);
  const [fullCard, setFullCard] = useState(null);
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState(false);

  // PIN modals: "enter" (view details) | "set" | "change"
  const [pinModal, setPinModal] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const fetchCard = useCallback(async () => {
    try {
      const res = await API.get("/cards/my-card");
      setCard(res.data?.card || null);
      setHasPin(res.data?.hasPin === true);
    } catch (err) {
      if (err.response?.status === 404) {
        setCard(null);
      } else {
        console.error("Card fetch error:", err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  const handleApply = () => {
    navigate("/cards/apply");
  };

  // ---------- activation ----------
  const handleRequestActivation = async () => {
    setBusy(true);
    try {
      const res = await API.post("/cards/request-activation");
      showToast(res.data?.message || "Activation requested");
      fetchCard();
    } catch (err) {
      showToast(err.response?.data?.message || "Could not request activation");
    } finally {
      setBusy(false);
    }
  };

  // ---------- reveal details (PIN gated) ----------
  const handleRevealClick = () => {
    if (revealed) {
      setRevealed(false);
      setFlipped(false);
      return;
    }
    if (!hasPin) {
      setPinModal("set"); // must create PIN before details can be viewed
      return;
    }
    setPinModal("enter");
  };

  const submitPinForDetails = async (pin) => {
    setBusy(true);
    try {
      const res = await API.get(`/cards/my-card/full?pin=${encodeURIComponent(pin)}`);
      setFullCard(res.data?.card || null);
      setRevealed(true);
      setPinModal(null);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === "PIN_NOT_SET") {
        setPinModal("set");
      } else {
        showToast(err.response?.data?.message || "Could not load card details");
      }
    } finally {
      setBusy(false);
    }
  };

  // ---------- set / change PIN (via /api/pin) ----------
  const submitSetPin = async ({ pin, confirmPin, currentPin }) => {
    setBusy(true);
    try {
      const body = currentPin ? { currentPin, pin, confirmPin } : { pin, confirmPin };
      const res = await API.post("/pin/set", body);
      showToast(res.data?.message || "PIN saved");
      setHasPin(true);
      setPinModal(null);
    } catch (err) {
      showToast(err.response?.data?.message || "Could not save PIN");
    } finally {
      setBusy(false);
    }
  };

  const handleFreezeToggle = async () => {
    try {
      const res = await API.post("/cards/toggle-freeze");
      setCard((c) => (c ? { ...c, status: res.data?.status || c.status } : c));
      showToast(res.data?.status === "frozen" ? "Card frozen" : "Card unfrozen");
    } catch (err) {
      showToast(err.response?.data?.message || "Action failed");
    }
  };

  const copyNumber = async () => {
    if (!fullCard?.cardNumber) {
      showToast("Show card details first");
      return;
    }
    try {
      await navigator.clipboard.writeText(fullCard.cardNumber.replace(/\s/g, ""));
      showToast("Card number copied");
    } catch {
      showToast("Copy failed");
    }
  };

  const fmtExpiry = (m, y) =>
    m && y ? `${String(m).padStart(2, "0")}/${String(y).slice(-2)}` : "••/••";

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#071032] to-black flex items-center justify-center">
        <p className="text-slate-300 animate-pulse">Loading your card...</p>
      </div>
    );
  }

  const status = card?.status || null;
  const tier = fullCard?.tier || card?.tier || "classic";
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
  const cardUsable = status === "active" || status === "frozen";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#071032] to-black text-white pb-28">
      {/* header */}
      <div className="max-w-lg mx-auto px-5 pt-7">
        <h1 className="text-2xl font-bold">My Card</h1>
        <p className="text-slate-400 text-sm mt-1">
          Your ProBank virtual card for online payments
        </p>
      </div>

      <div className="max-w-lg mx-auto px-5 mt-6">
        {/* ============ NO CARD — PREVIEW + APPLY ============ */}
        {!card && (
          <>
            <VirtualCardDisplay
              tier="classic"
              holderName={JSON.parse(localStorage.getItem("user") || "{}")?.name || "PROBANK USER"}
              status="active"
              flipped={previewFlipped}
              onFlip={() => setPreviewFlipped((f) => !f)}
            />

            <div className="mt-7 rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/10">
              <BenefitRow
                icon="🌍"
                title="Pay online anywhere"
                text="Use it on websites and apps that accept card payments"
              />
              <BenefitRow
                icon="🛡️"
                title="You're in control"
                text="Freeze and unfreeze your card instantly, anytime"
              />
              <BenefitRow
                icon="💳"
                title="Three card tiers"
                text="Classic $43 • Gold $55 • Platinum $75 — one-time fee"
              />
            </div>

            <button
              onClick={handleApply}
              className="mt-6 w-full py-4 rounded-2xl bg-gradient-to-r from-amber-400 to-amber-600 text-black font-bold text-base shadow-lg shadow-amber-500/20 active:scale-[0.98] transition"
            >
              Apply for Virtual Card
            </button>

            <p className="text-center text-[11px] text-slate-500 mt-3">
              From $43 one-time • Manual payment • Activated by support
            </p>
          </>
        )}

        {/* ============ HAS CARD ============ */}
        {card && (
          <>
            <VirtualCardDisplay
              tier={tier}
              cardNumber={
                revealed && fullCard?.cardNumber
                  ? fullCard.cardNumber
                  : card.maskedNumber || card.cardNumber
              }
              holderName={card.cardholderName || "PROBANK USER"}
              expiry={fmtExpiry(card.expiryMonth, card.expiryYear)}
              cvv={revealed && fullCard?.cvv ? fullCard.cvv : "•••"}
              status={status === "approved" || status === "activation_requested" ? "pending" : status}
              revealed={revealed}
              flipped={flipped}
              onFlip={() => {
                if (!cardUsable) return;
                if (!revealed) {
                  showToast("Enter your PIN to view details first");
                  return;
                }
                setFlipped((f) => !f);
              }}
            />

            {/* ---- status banners ---- */}

            {status === "pending" && (
              <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
                <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm">
                  <span>⏳</span> {tierLabel} application under review
                </div>
                <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">
                  Your card will be approved once your payment of{" "}
                  <b className="text-amber-200">
                    {card.feeCurrency === "GHS" ? "GH₵" : "₦"}
                    {(card.feeAmount || 0).toLocaleString()}
                  </b>{" "}
                  is confirmed by support. You'll get a notification.
                </p>
                <div className="flex items-center gap-2 mt-4">
                  <StepDot done label="Applied" />
                  <StepLine />
                  <StepDot active label="Payment Review" />
                  <StepLine />
                  <StepDot label="Activate" />
                </div>
              </div>
            )}

            {status === "approved" && (
              <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                <div className="flex items-center gap-2 text-emerald-300 font-semibold text-sm">
                  <span>✅</span> Your {tierLabel} card is ready
                </div>
                <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">
                  Your payment has been confirmed. Tap below to request activation — support
                  will switch your card on, then you can set your card PIN.
                </p>
                <button
                  onClick={handleRequestActivation}
                  disabled={busy}
                  className="mt-4 w-full py-3.5 rounded-2xl bg-gradient-to-r from-emerald-400 to-emerald-600 text-black font-bold text-sm shadow-lg shadow-emerald-500/20 active:scale-[0.98] transition disabled:opacity-60"
                >
                  {busy ? "Please wait..." : "Activate Your Card"}
                </button>
                <div className="flex items-center gap-2 mt-4">
                  <StepDot done label="Applied" />
                  <StepLine />
                  <StepDot done label="Paid" />
                  <StepLine />
                  <StepDot active label="Activate" />
                </div>
              </div>
            )}

            {status === "activation_requested" && (
              <div className="mt-6 rounded-2xl border border-sky-500/30 bg-sky-500/10 p-4">
                <div className="flex items-center gap-2 text-sky-300 font-semibold text-sm">
                  <span>🕓</span> Activation in progress
                </div>
                <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">
                  Support has your activation request and will switch your card on shortly.
                  You'll get a notification the moment it's active.
                </p>
                <div className="flex items-center gap-2 mt-4">
                  <StepDot done label="Applied" />
                  <StepLine />
                  <StepDot done label="Paid" />
                  <StepLine />
                  <StepDot active label="Activating" />
                </div>
              </div>
            )}

            {status === "rejected" && (
              <div className="mt-6 rounded-2xl border border-red-500/30 bg-red-500/10 p-4">
                <div className="text-red-300 font-semibold text-sm">Application declined</div>
                <p className="text-slate-400 text-xs mt-1.5">
                  Please contact support for more information, or apply again.
                </p>
                <div className="flex gap-3 mt-3">
                  <button
                    onClick={() => navigate("/support")}
                    className="flex-1 py-2.5 rounded-xl bg-white/10 text-sm font-semibold hover:bg-white/15 transition"
                  >
                    Contact Support
                  </button>
                  <button
                    onClick={handleApply}
                    className="flex-1 py-2.5 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 text-sm font-semibold hover:bg-amber-500/30 transition"
                  >
                    Apply Again
                  </button>
                </div>
              </div>
            )}

            {cardUsable && (
              <>
                {/* balance strip */}
                <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 px-5 py-4 flex items-center justify-between">
                  <div>
                    <div className="text-[11px] text-slate-400 tracking-wide">
                      {tierLabel.toUpperCase()} CARD BALANCE
                    </div>
                    <div className="text-xl font-bold mt-0.5">
                      {card.currency === "GHS" ? "GH₵" : "₦"}
                      {(card.balance ?? 0).toLocaleString()}
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-[11px] font-bold tracking-wide ${
                      status === "active"
                        ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                        : "bg-sky-500/15 text-sky-300 border border-sky-500/30"
                    }`}
                  >
                    {status === "active" ? "ACTIVE" : "FROZEN"}
                  </span>
                </div>

                {/* create-PIN prompt (first time after activation) */}
                {!hasPin && (
                  <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
                    <div className="text-amber-300 font-semibold text-sm">
                      Create your card PIN
                    </div>
                    <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">
                      Your card is active. Set a 4-digit PIN to secure it — you'll need it to
                      view your card details and confirm transactions, just like a bank card.
                    </p>
                    <button
                      onClick={() => setPinModal("set")}
                      className="mt-3 w-full py-3 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-black font-bold text-sm active:scale-[0.98] transition"
                    >
                      Set Card PIN
                    </button>
                  </div>
                )}

                {/* quick actions */}
                <div className="mt-5 grid grid-cols-3 gap-3">
                  <ActionButton
                    label={revealed ? "Hide Details" : "Show Details"}
                    icon={
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    }
                    onClick={handleRevealClick}
                  />
                  <ActionButton
                    label="Copy Number"
                    icon={
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="12" height="12" rx="2" />
                        <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                      </svg>
                    }
                    onClick={copyNumber}
                  />
                  <ActionButton
                    label={status === "frozen" ? "Unfreeze" : "Freeze"}
                    icon={
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2v20M4 6l16 12M20 6L4 18M2 12h20" />
                      </svg>
                    }
                    onClick={handleFreezeToggle}
                  />
                </div>

                {/* change PIN row (only once they have one) */}
                {hasPin && (
                  <button
                    onClick={() => setPinModal("change")}
                    className="mt-3 w-full py-3 rounded-xl bg-white/5 border border-white/10 text-slate-300 text-sm font-semibold hover:bg-white/10 transition"
                  >
                    🔒 Change Card PIN
                  </button>
                )}

                {/* revealed details panel */}
                {revealed && fullCard && (
                  <div className="mt-5 rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/10">
                    <DetailRow label="Card Number" value={fullCard.cardNumber} mono />
                    <DetailRow label="Expiry" value={fmtExpiry(card.expiryMonth, card.expiryYear)} mono />
                    <DetailRow label="CVV" value={fullCard.cvv} mono />
                    <DetailRow label="Card Holder" value={card.cardholderName} />
                  </div>
                )}

                {/* transactions */}
                <div className="mt-7">
                  <h3 className="text-sm font-semibold text-slate-300 mb-3">Card Transactions</h3>
                  <div className="rounded-2xl bg-white/5 border border-white/10">
                    {(card.transactions || []).length === 0 ? (
                      <div className="py-10 text-center">
                        <div className="text-2xl mb-2">💳</div>
                        <p className="text-slate-500 text-xs">No card transactions yet</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-white/10">
                        {(card.transactions || [])
                          .slice()
                          .reverse()
                          .map((tx, i) => (
                            <div key={i} className="flex items-center justify-between px-4 py-3.5">
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">
                                  {tx.description || "Card transaction"}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">
                                  {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : ""}
                                </div>
                              </div>
                              <div
                                className={`text-sm font-semibold ml-3 ${
                                  tx.type === "spend" ? "text-red-400" : "text-emerald-300"
                                }`}
                              >
                                {tx.type === "spend" ? "-" : "+"}
                                {card.currency === "GHS" ? "GH₵" : "₦"}
                                {Math.abs(tx.amount || 0).toLocaleString()}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* PIN modals */}
      {pinModal === "enter" && (
        <PinEntryModal busy={busy} onSubmit={submitPinForDetails} onClose={() => setPinModal(null)} />
      )}
      {pinModal === "set" && (
        <PinSetModal busy={busy} onSubmit={submitSetPin} onClose={() => setPinModal(null)} />
      )}
      {pinModal === "change" && (
        <PinSetModal busy={busy} changing onSubmit={submitSetPin} onClose={() => setPinModal(null)} />
      )}

      {/* toast */}
      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-24 z-50 px-5 py-3 rounded-full bg-black/90 border border-white/15 text-sm shadow-2xl whitespace-nowrap">
          {toast}
        </div>
      )}

      <BottomNav />
    </div>
  );
}

// ---------- small presentational pieces ----------

function BenefitRow({ icon, title, text }) {
  return (
    <div className="flex items-start gap-4 px-4 py-4">
      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-lg flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-slate-400 text-xs mt-0.5 leading-relaxed">{text}</div>
      </div>
    </div>
  );
}

function ActionButton({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 py-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 active:scale-95 transition"
    >
      <span className="text-amber-400">{icon}</span>
      <span className="text-[11px] font-medium text-slate-300">{label}</span>
    </button>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex justify-between items-center px-4 py-3.5">
      <span className="text-slate-400 text-xs">{label}</span>
      <span className={`text-sm font-semibold ${mono ? "font-mono tracking-wider" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function StepDot({ done, active, label }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`w-3 h-3 rounded-full ${
          done ? "bg-emerald-400" : active ? "bg-amber-400 animate-pulse" : "bg-white/20"
        }`}
      />
      <span className="text-[9px] text-slate-400">{label}</span>
    </div>
  );
}

function StepLine() {
  return <div className="flex-1 h-px bg-white/15 -mt-3" />;
}

// ---------- PIN modals ----------

function PinInput({ value, onChange, autoFocus, placeholder }) {
  return (
    <input
      type="password"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={4}
      value={value}
      autoFocus={autoFocus}
      placeholder={placeholder || "••••"}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
      className="w-full text-center text-2xl tracking-[0.6em] font-mono bg-white/5 border border-white/15 rounded-xl py-3.5 outline-none focus:border-amber-400/60 transition placeholder:text-slate-600 placeholder:tracking-[0.3em]"
    />
  );
}

function PinEntryModal({ busy, onSubmit, onClose }) {
  const [pin, setPin] = useState("");

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-3xl bg-[#0b1226] border border-white/10 p-6">
        <h3 className="font-bold text-lg">Enter Card PIN</h3>
        <p className="text-slate-400 text-xs mt-1">
          Your 4-digit PIN is required to view your card details.
        </p>
        <div className="mt-5">
          <PinInput value={pin} onChange={setPin} autoFocus />
        </div>
        <button
          onClick={() => pin.length === 4 && onSubmit(pin)}
          disabled={busy || pin.length !== 4}
          className="mt-5 w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-black font-bold text-sm active:scale-[0.98] transition disabled:opacity-50"
        >
          {busy ? "Verifying..." : "View Card Details"}
        </button>
        <button
          onClick={onClose}
          className="mt-3 w-full py-2.5 text-slate-400 text-sm hover:text-white transition"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function PinSetModal({ busy, changing, onSubmit, onClose }) {
  const [currentPin, setCurrentPin] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  const valid =
    pin.length === 4 &&
    confirmPin.length === 4 &&
    pin === confirmPin &&
    (!changing || currentPin.length === 4);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-3xl bg-[#0b1226] border border-white/10 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="font-bold text-lg">{changing ? "Change Card PIN" : "Create Card PIN"}</h3>
        <p className="text-slate-400 text-xs mt-1">
          {changing
            ? "Enter your current PIN, then choose a new one."
            : "Choose a 4-digit PIN. You'll use it to view card details and confirm transactions."}
        </p>

        <div className="mt-5 space-y-4">
          {changing && (
            <div>
              <label className="text-[11px] text-slate-400 mb-1.5 block">CURRENT PIN</label>
              <PinInput value={currentPin} onChange={setCurrentPin} autoFocus />
            </div>
          )}
          <div>
            <label className="text-[11px] text-slate-400 mb-1.5 block">
              {changing ? "NEW PIN" : "PIN"}
            </label>
            <PinInput value={pin} onChange={setPin} autoFocus={!changing} />
          </div>
          <div>
            <label className="text-[11px] text-slate-400 mb-1.5 block">
              CONFIRM {changing ? "NEW " : ""}PIN
            </label>
            <PinInput value={confirmPin} onChange={setConfirmPin} />
          </div>
        </div>

        {pin.length === 4 && confirmPin.length === 4 && pin !== confirmPin && (
          <p className="text-red-400 text-xs mt-3">PINs do not match.</p>
        )}

        <button
          onClick={() => valid && onSubmit({ pin, confirmPin, currentPin: changing ? currentPin : undefined })}
          disabled={busy || !valid}
          className="mt-5 w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-black font-bold text-sm active:scale-[0.98] transition disabled:opacity-50"
        >
          {busy ? "Saving..." : changing ? "Change PIN" : "Set PIN"}
        </button>
        <button
          onClick={onClose}
          className="mt-3 w-full py-2.5 text-slate-400 text-sm hover:text-white transition"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
